<template>
    <Teleport
        v-if="inputTarget"
        :to="inputTarget"
    >
        <span
            v-if="isTypingVisible"
            data-testid="onboarding-input-example-guide-typing"
            class="onboarding-input-example-guide__typing"
            aria-hidden="true"
        >
            <span>{{ displayedText }}</span>
            <span
                data-testid="onboarding-input-example-guide-cursor"
                class="onboarding-input-example-guide__cursor"
                :class="{
                    'onboarding-input-example-guide__cursor--finishing': phase === 'cursor-pausing',
                    'onboarding-input-example-guide__cursor--hidden': !isCursorVisible,
                }"
            />
        </span>
    </Teleport>
    <Teleport :to="exampleTarget">
        <span
            v-if="isGestureVisible"
            data-testid="onboarding-input-example-guide-gesture"
            class="onboarding-input-example-guide__gesture"
            aria-hidden="true"
        >
            <OnboardingClickGesture
                :clickCount="2"
                @complete="handleGestureComplete"
            />
        </span>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import OnboardingClickGesture from './OnboardingClickGesture.vue';
import type { OnboardingInputExampleGuideMode } from '../services/onboarding-input-example-guide-presenter';

/** 拉丁字符逐字出现间隔，接近日常键盘输入的视觉速度。 */
const LATIN_TYPING_INTERVAL_MS = 65;
/** 中日韩字符逐字出现间隔，模拟输入法确认文字后的阅读节奏。 */
const CJK_TYPING_INTERVAL_MS = 110;
/** 文案输入完成后光标显隐切换的间隔。 */
const FINAL_CURSOR_TOGGLE_INTERVAL_MS = 250;
/** 文案输入完成后光标需要明确闪烁的次数。 */
const FINAL_CURSOR_BLINK_COUNT = 3;
/** 三次闪烁结束后额外保留最终可见光标的时间。 */
const FINAL_CURSOR_HOLD_DURATION_MS = 250;
/** 三次光标闪烁完成前保留完整模拟文字的总时长。 */
const FINAL_CURSOR_BLINK_TOTAL_MS = (
    FINAL_CURSOR_TOGGLE_INTERVAL_MS * FINAL_CURSOR_BLINK_COUNT * 2
    + FINAL_CURSOR_HOLD_DURATION_MS
);
/** 手势阶段为示例补充的显式 hover 状态。 */
const EXAMPLE_ACTIVE_CLASS = 'onboarding-input-example-guide-example-active';
/** 模拟打字期间隐藏原生 textarea placeholder 的临时状态。 */
const PLACEHOLDER_HIDDEN_CLASS = 'onboarding-input-example-guide-placeholder-hidden';

type GuidePhase = 'typing' | 'cursor-pausing' | 'gesture' | 'complete' | 'static';

interface Props {
    /** 第一条示例元素，用于承载小手和点击圈。 */
    exampleTarget: HTMLElement;
    /** 当前语言和数据下的第一条示例文案。 */
    exampleText?: string;
    /** 输入框内部的定位容器，仅打字模式需要。 */
    inputTarget?: HTMLElement;
    /** 当前演示使用的交互模式。 */
    mode: OnboardingInputExampleGuideMode;
}

const props = defineProps<Props>();
const displayedText = ref('');
const isCursorVisible = ref(true);
const phase = ref<GuidePhase>(props.mode === 'gesture-only' ? 'gesture' : 'typing');
const timeoutIds = new Set<number>();
let typingIntervalId: number | undefined;

const isTypingVisible = computed(() => (
    props.mode === 'typing-and-gesture'
    && (phase.value === 'typing' || phase.value === 'cursor-pausing')
));
const isGestureVisible = computed(() => phase.value === 'gesture' || phase.value === 'static');

/** 创建可统一清理的一次性定时任务。 */
function schedule(callback: () => void, delayMs: number): void {
    const timeoutId = window.setTimeout(() => {
        timeoutIds.delete(timeoutId);
        callback();
    }, delayMs);
    timeoutIds.add(timeoutId);
}

/** 停止当前打字刷新任务。 */
function clearTypingInterval(): void {
    if (typingIntervalId === undefined) return;
    window.clearInterval(typingIntervalId);
    typingIntervalId = undefined;
}

/** 进入复用的小手点击演示阶段。 */
function startGestureSequence(): void {
    clearTypingInterval();
    displayedText.value = '';
    isCursorVisible.value = true;
    phase.value = 'gesture';
    props.inputTarget?.classList.remove(PLACEHOLDER_HIDDEN_CLASS);
    props.exampleTarget.classList.add(EXAMPLE_ACTIVE_CLASS);
}

/** 公共双击手势播放完成后结束本轮视觉演示。 */
function handleGestureComplete(): void {
    if (phase.value !== 'gesture') return;
    props.exampleTarget.classList.remove(EXAMPLE_ACTIVE_CLASS);
    phase.value = 'complete';
}

/** 根据字符类型返回接近真人输入观感的固定逐字间隔。 */
function getTypingInterval(text: string): number {
    return /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(text)
        ? CJK_TYPING_INTERVAL_MS
        : LATIN_TYPING_INTERVAL_MS;
}

/** 用显式显隐状态播放三次可测试、肉眼清晰的完成态光标闪烁。 */
function startFinalCursorBlinks(): void {
    phase.value = 'cursor-pausing';
    isCursorVisible.value = true;
    const toggleCount = FINAL_CURSOR_BLINK_COUNT * 2;
    for (let toggleIndex = 1; toggleIndex <= toggleCount; toggleIndex += 1) {
        schedule(() => {
            isCursorVisible.value = toggleIndex % 2 === 0;
        }, FINAL_CURSOR_TOGGLE_INTERVAL_MS * toggleIndex);
    }
    schedule(startGestureSequence, FINAL_CURSOR_BLINK_TOTAL_MS);
}

/** 按固定逐字速度展示当前示例预览文案。 */
function startTypingSequence(): void {
    clearTypingInterval();
    phase.value = 'typing';
    displayedText.value = '';
    isCursorVisible.value = true;
    props.inputTarget?.classList.add(PLACEHOLDER_HIDDEN_CLASS);
    const characters = Array.from(props.exampleText ?? '');
    if (characters.length === 0) {
        startGestureSequence();
        return;
    }

    let characterIndex = 0;
    typingIntervalId = window.setInterval(() => {
        characterIndex += 1;
        displayedText.value = characters.slice(0, characterIndex).join('');
        if (characterIndex < characters.length) return;
        clearTypingInterval();
        startFinalCursorBlinks();
    }, getTypingInterval(characters.join('')));
}

/** 判断系统是否要求减少动态效果。 */
function prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** 清理组件生命周期内创建的全部定时器。 */
function clearTimers(): void {
    clearTypingInterval();
    timeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    timeoutIds.clear();
}

onMounted(() => {
    if (prefersReducedMotion()) {
        phase.value = 'static';
        props.exampleTarget.classList.add(EXAMPLE_ACTIVE_CLASS);
        return;
    }
    if (props.mode === 'typing-and-gesture') {
        startTypingSequence();
    } else {
        props.exampleTarget.classList.add(EXAMPLE_ACTIVE_CLASS);
    }
});

onBeforeUnmount(() => {
    props.exampleTarget.classList.remove(EXAMPLE_ACTIVE_CLASS);
    props.inputTarget?.classList.remove(PLACEHOLDER_HIDDEN_CLASS);
    clearTimers();
});
</script>

<style lang="scss">
.onboarding-input-example-guide-host {
    display: contents;
}

.onboarding-input-example-guide-input-target,
.onboarding-input-example-guide-example-target {
    position: relative !important;
}

.onboarding-input-example-guide-example-target {
    overflow: visible !important;
}

.onboarding-input-example-guide-input-target.onboarding-input-example-guide-placeholder-hidden
> textarea::placeholder {
    color: transparent !important;
    opacity: 0 !important;
}

.onboarding-input-example-guide-example-target.onboarding-input-example-guide-example-active.agent-example-item,
.onboarding-input-example-guide-example-target.onboarding-input-example-guide-example-active
> .agent-example-item-image {
    background-color: #f0f6ff !important;
}
</style>

<style scoped lang="scss">
.onboarding-input-example-guide__typing {
    position: absolute;
    top: 16px;
    right: 16px;
    left: 16px;
    z-index: 2;
    overflow: hidden;
    color: #495973;
    font-size: 14px;
    line-height: 20px;
    text-overflow: clip;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    background: #fff;
    pointer-events: none;
}

.onboarding-input-example-guide__gesture {
    position: absolute;
    top: 50%;
    right: -10px;
    z-index: 4;
    display: block;
    width: 68px;
    height: 65px;
    pointer-events: none;
    transform: translateY(-19px);
}

.onboarding-input-example-guide__cursor {
    display: inline-block;
    width: 1.5px;
    height: 16px;
    margin-left: 2px;
    vertical-align: -3px;
    background: #8993a4;
    animation: onboarding-input-example-guide-cursor-blink 800ms step-end infinite;

    &--finishing {
        animation: none;
    }

    &--hidden {
        opacity: 0;
    }
}

@keyframes onboarding-input-example-guide-cursor-blink {
    0%,
    45% {
        opacity: 1;
    }

    50%,
    100% {
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .onboarding-input-example-guide__cursor {
        animation: none;
    }
}
</style>
